import { useCallback } from 'react';

import { useShouldShowAtsVacancyModal } from '@/shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/useShouldShowAtsVacancyModal';
import { useGetAllAtsConnectionsQuery } from 'shared/legacy/hooks/useGetAllAtsConnectionsQuery';
import { OutreachType } from 'types/outreach';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import {
  GlobalDialogType,
  type SelectAtsVacancyForCampaignDialogData,
} from 'stores/global-dialogs/global-dialogs.store.types';
import { SelectAtsVacancyModalType } from './SelectAtsVacancyForCampaignModal.types';

type Options = {
  openAction: (payload?: number) => void | Promise<unknown> | unknown;
  candidateFirstName?: string;
  type: SelectAtsVacancyModalType;
  channel?: OutreachType;
  onCloseButtonClick?: () => void;
};

export const useOpenWithAtsVacancyPrompt = (options: Options) => {
  const modal = useGlobalDialog(GlobalDialogType.SelectAtsVacancyForCampaign);

  const { openAction, candidateFirstName, type, channel, onCloseButtonClick } = options;
  const { data: atsConnections } = useGetAllAtsConnectionsQuery();
  const shouldShowAtsModal = useShouldShowAtsVacancyModal({
    variant: type,
    channel,
  });

  const open = useCallback(
    (options: SelectAtsVacancyForCampaignDialogData = {}) => {
      modal.open({ payload: options });
    },
    [modal],
  );

  return useCallback(
    (payload?: number): Promise<unknown> => {
      if (shouldShowAtsModal) {
        return new Promise((resolve, reject) => {
          open({
            candidateFirstName,
            atsName: atsConnections?.activeATS?.ats_name || undefined,
            type,
            onConfirm: async () => {
              try {
                resolve(await openAction(payload));
              } catch (error) {
                reject(error);
              }
            },
            onClose: () => {
              Promise.resolve(openAction(payload)).then(resolve).catch(reject);
            },
            onCloseButtonClick,
          });
        });
      }
      return Promise.resolve(openAction(payload));
    },
    [
      shouldShowAtsModal,
      open,
      candidateFirstName,
      atsConnections?.activeATS?.ats_name,
      type,
      openAction,
      onCloseButtonClick,
    ],
  );
};
